import * as React from "react";
import mountainsImg from "@/assets/mountains.png";
import cloud1 from "@/assets/cloud-1.png";
import cloud2 from "@/assets/cloud-2.png";
import cloud3 from "@/assets/cloud-3.png";
import cloud4 from "@/assets/cloud-4.png";
import cloud5 from "@/assets/cloud-5.png";
import cloud6 from "@/assets/cloud-6.png";
import cloud7 from "@/assets/cloud-7.png";
import cloud8 from "@/assets/cloud-8.png";

/**
 * CloudStratum — full-bleed cloud backdrop for the Fit form.
 *
 * Two cloud fields are rendered ALWAYS:
 *   1. Drift field — clouds sweep horizontally to the left at varied
 *      per-cloud rates. Active resting state at step 0 (matches the calm
 *      sky in the hero video).
 *   2. Flythrough field — clouds spawn at deep distance, grow as they
 *      drift downward and outward, then pass the camera. Active resting
 *      state at step 1+ with an answer-triggered playbackRate burst on
 *      every step change.
 *
 * Both fields run their Web Animations continuously. The CURRENTLY-VISIBLE
 * field is controlled by a CSS opacity cross-fade (~1.4s). When the user
 * clicks "Fly forward" out of step 0, the drift field fades out while the
 * flythrough field fades in, and at the same instant the flythrough
 * animations' playbackRate jumps to 6× for the burst window — so the
 * clouds the user sees materialising are already mid-whoosh, no hard cut.
 *
 * `prefers-reduced-motion` skips all motion: animations are cancelled and
 * the appropriate field renders a single static frame.
 */

interface CloudStratumProps {
  step: number;
}

interface CloudSpec {
  // Flythrough origin (step 1+).
  spawnX: number;
  spawnY: number;
  exitX: number;
  exitY: number;
  size: number;
  // Drift placement (step 0). Y is altitude in the sky and size shrinks
  // toward both the top and bottom — biggest clouds sit in the mid band.
  driftY: number;
  driftSize: number;
  phaseOffset: number;
  image: string;
}

// 8 clouds, one PNG each. Drift Y values fill the sky from ~12vh down to
// ~65vh (kept above the mountain horizon); drift sizes peak around y≈40vh
// and taper at both extremes for atmospheric depth.
const CLOUDS: CloudSpec[] = [
  {
    spawnX: 22,
    spawnY: 22,
    exitX: -8,
    exitY: 90,
    size: 480,
    driftY: 12,
    driftSize: 300,
    phaseOffset: 0.45,
    image: cloud3,
  },
  {
    spawnX: 72,
    spawnY: 18,
    exitX: 108,
    exitY: 92,
    size: 520,
    driftY: 56,
    driftSize: 380,
    phaseOffset: 0.65,
    image: cloud7,
  },
  {
    spawnX: 50,
    spawnY: 28,
    exitX: 42,
    exitY: 95,
    size: 540,
    driftY: 42,
    driftSize: 520,
    phaseOffset: 0.85,
    image: cloud1,
  },
  {
    spawnX: 14,
    spawnY: 32,
    exitX: -22,
    exitY: 78,
    size: 400,
    driftY: 50,
    driftSize: 440,
    phaseOffset: 0.2,
    image: cloud5,
  },
  {
    spawnX: 86,
    spawnY: 30,
    exitX: 122,
    exitY: 80,
    size: 420,
    driftY: 36,
    driftSize: 540,
    phaseOffset: 0.1,
    image: cloud2,
  },
  {
    spawnX: 40,
    spawnY: 14,
    exitX: 18,
    exitY: 92,
    size: 360,
    driftY: 30,
    driftSize: 480,
    phaseOffset: 0.55,
    image: cloud8,
  },
  {
    spawnX: 62,
    spawnY: 36,
    exitX: 78,
    exitY: 88,
    size: 460,
    driftY: 18,
    driftSize: 360,
    phaseOffset: 0.75,
    image: cloud4,
  },
  {
    spawnX: 32,
    spawnY: 12,
    exitX: 2,
    exitY: 94,
    size: 340,
    driftY: 65,
    driftSize: 290,
    phaseOffset: 0.05,
    image: cloud6,
  },
];

const skyGradients = [
  // 0 — takeoff: matches Hero video sky (sky-blue → warm cream)
  "linear-gradient(to bottom, #7dc4cc 0%, #ffeecb 100%)",
  // 1 — late morning: cooler top, sage ground
  "linear-gradient(to bottom, #D9E6F0 0%, #DCDCC0 50%, #9AB0A2 100%)",
  // 2 — golden hour into evening: peachy top, warm horizon
  "linear-gradient(to bottom, #C49EAA 0%, #E5C094 50%, #7C5E5E 100%)",
  // 3 — night with stars emerging: deep blue top, mauve mid, warm horizon
  "linear-gradient(to bottom, #26345D 0%, #5E4F6E 50%, #A88876 100%)",
  // 4 — full starry night: deeper top, taupe horizon
  "linear-gradient(to bottom, #0E1530 0%, #2E2D58 50%, #7E6E68 100%)",
];

const cloudOpacityByStep = [1, 1, 0.7, 0.35, 0.18];
const mountainOpacityByStep = [1, 0, 0, 0, 0];
// Anchored at center-bottom: scale grows the range upward while translateY
// pushes it past the camera, mimicking flying low over the peaks.
const mountainTransformByStep = [
  "scale(1) translateY(0%)",
  "scale(2.8) translateY(70%)",
  "scale(2.8) translateY(70%)",
  "scale(2.8) translateY(70%)",
  "scale(2.8) translateY(70%)",
];
const starOpacityByStep = [0, 0, 0.45, 0.95, 1];
const inkInvertedByStep = [false, false, false, true, true];

const AMBIENT_DURATION_MS = 28_000;
const BURST_RATE = 6;
// Synced to the .animate-card-approach duration (850ms in globals.css) so
// the whoosh resolves at the exact moment the new step's text lands.
const BURST_MS = 850;
const FIELD_FADE_MS = 850;

function driftDurationMs(cloud: CloudSpec): number {
  // Larger (closer) clouds drift faster — more parallax.
  // driftSize 540 → ~55s; driftSize 290 → ~80s.
  return 110_000 - cloud.driftSize * 100;
}

function buildFlythroughKeyframes(cloud: CloudSpec): Keyframe[] {
  return [
    {
      offset: 0,
      transform: `translate(${cloud.spawnX}vw, ${cloud.spawnY}vh) translate(-50%, -50%) scale(0.3)`,
      opacity: 0,
      filter: "blur(2px)",
    },
    { offset: 0.15, opacity: 0.85, filter: "blur(0)" },
    { offset: 0.85, opacity: 0.9, filter: "blur(0)" },
    {
      offset: 1,
      transform: `translate(${cloud.exitX}vw, ${cloud.exitY}vh) translate(-50%, -50%) scale(2.4)`,
      opacity: 0,
      filter: "blur(0)",
    },
  ];
}

function buildDriftKeyframes(cloud: CloudSpec): Keyframe[] {
  return [
    {
      offset: 0,
      transform: `translate(${cloud.spawnX + 110}vw, ${cloud.driftY}vh) translate(-50%, -50%) scale(0.95)`,
      opacity: 0,
      filter: "blur(0)",
    },
    { offset: 0.1, opacity: 0.85 },
    { offset: 0.9, opacity: 0.85 },
    {
      offset: 1,
      transform: `translate(${cloud.spawnX - 110}vw, ${cloud.driftY}vh) translate(-50%, -50%) scale(0.95)`,
      opacity: 0,
      filter: "blur(0)",
    },
  ];
}

export function CloudStratum({ step }: CloudStratumProps) {
  const driftRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const flyRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const driftAnims = React.useRef<Array<Animation | null>>([]);
  const flyAnims = React.useRef<Array<Animation | null>>([]);
  const prevStepRef = React.useRef(step);
  const [reducedMotion, setReducedMotion] = React.useState(false);

  // Detect reduced-motion preference.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReducedMotion(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Attach (or re-attach after Strict-Mode unmount) the drift animations.
  React.useEffect(() => {
    if (reducedMotion) {
      driftAnims.current.forEach((a) => a?.cancel());
      driftAnims.current = [];
      return;
    }
    driftRefs.current.forEach((el, i) => {
      if (!el) return;
      if (driftAnims.current[i]) return;
      const cloud = CLOUDS[i]!;
      const duration = driftDurationMs(cloud);
      const anim = el.animate(buildDriftKeyframes(cloud), {
        duration,
        iterations: Infinity,
        easing: "linear",
        fill: "both",
      });
      anim.currentTime = cloud.phaseOffset * duration;
      driftAnims.current[i] = anim;
    });
    return () => {
      driftAnims.current.forEach((a) => a?.cancel());
      driftAnims.current = [];
    };
  }, [reducedMotion]);

  // Attach (or re-attach) the flythrough animations.
  React.useEffect(() => {
    if (reducedMotion) {
      flyAnims.current.forEach((a) => a?.cancel());
      flyAnims.current = [];
      return;
    }
    flyRefs.current.forEach((el, i) => {
      if (!el) return;
      if (flyAnims.current[i]) return;
      const cloud = CLOUDS[i]!;
      const anim = el.animate(buildFlythroughKeyframes(cloud), {
        duration: AMBIENT_DURATION_MS,
        iterations: Infinity,
        easing: "linear",
        fill: "both",
      });
      anim.currentTime = cloud.phaseOffset * AMBIENT_DURATION_MS;
      flyAnims.current[i] = anim;
    });
    return () => {
      flyAnims.current.forEach((a) => a?.cancel());
      flyAnims.current = [];
    };
  }, [reducedMotion]);

  // Step transitions: bump the flythrough field's playbackRate so the
  // newly-visible clouds materialise mid-whoosh. Only fires when the
  // target step is in flythrough mode (step > 0); drift mode stays calm.
  React.useEffect(() => {
    if (reducedMotion) {
      prevStepRef.current = step;
      return;
    }
    const prev = prevStepRef.current;
    prevStepRef.current = step;
    if (step === prev) return;
    if (step === 0) return; // drift mode: no burst

    const direction = step > prev ? 1 : -1;
    const targetRate = direction * BURST_RATE;
    flyAnims.current.forEach((a) => {
      if (a) a.playbackRate = targetRate;
    });
    const t = window.setTimeout(() => {
      flyAnims.current.forEach((a) => {
        if (a) a.playbackRate = 1;
      });
    }, BURST_MS);
    return () => window.clearTimeout(t);
  }, [step, reducedMotion]);

  const stepIdx = Math.min(step, skyGradients.length - 1);
  const isDrift = step === 0;
  const baseCloudOpacity = cloudOpacityByStep[stepIdx]!;
  const driftFieldOpacity = isDrift ? baseCloudOpacity : 0;
  const flyFieldOpacity = isDrift ? 0 : baseCloudOpacity;

  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Step-driven sky gradient. CSS can't interpolate between two
          `background-image` values, so we stack one layer per step in DOM
          order and only fade the new layer in on top. Previous layers stay
          at opacity 1 underneath so during a transition the visible blend
          is the new gradient over the previous gradient — no leak from the
          (lighter) page background through partially-transparent layers. */}
      {skyGradients.map((gradient, i) => (
        <div
          key={`sky-${i}`}
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background: gradient,
            opacity: i <= stepIdx ? 1 : 0,
            transition: "opacity 2.4s cubic-bezier(0.22, 1, 0.36, 1)",
          }}
        />
      ))}

      {/* Star field — opacity tied to step so stars emerge as we ascend. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          opacity: starOpacityByStep[stepIdx],
          transition: "opacity 2.4s cubic-bezier(0.22, 1, 0.36, 1)",
        }}
      >
        <Stars />
      </div>

      {/* DRIFT cloud field — visible at step 0, fades out on transition. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          opacity: driftFieldOpacity,
          transition: `opacity ${FIELD_FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {CLOUDS.map((cloud, i) => (
          <div
            key={`drift-${i}`}
            ref={(el) => {
              driftRefs.current[i] = el;
            }}
            className="absolute top-0 left-0"
            style={{
              width: cloud.driftSize,
              opacity: reducedMotion ? 0.85 : 0,
              willChange: "transform, opacity, filter",
            }}
          >
            <img
              src={cloud.image}
              alt=""
              aria-hidden="true"
              draggable={false}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        ))}
      </div>

      {/* FLYTHROUGH cloud field — invisible at step 0, fades in when the
          user clicks forward. Animations are always running underneath
          so the burst playbackRate puts them mid-whoosh as they appear. */}
      <div
        aria-hidden="true"
        className="absolute inset-0"
        style={{
          opacity: flyFieldOpacity,
          transition: `opacity ${FIELD_FADE_MS}ms cubic-bezier(0.22, 1, 0.36, 1)`,
        }}
      >
        {CLOUDS.map((cloud, i) => (
          <div
            key={`fly-${i}`}
            ref={(el) => {
              flyRefs.current[i] = el;
            }}
            className="absolute top-0 left-0"
            style={{
              width: cloud.size,
              opacity: reducedMotion ? 0.85 : 0,
              willChange: "transform, opacity, filter",
            }}
          >
            <img
              src={cloud.image}
              alt=""
              aria-hidden="true"
              draggable={false}
              style={{ width: "100%", height: "auto", display: "block" }}
            />
          </div>
        ))}
      </div>

      {/* Mountain horizon — in front of both cloud fields. On the step 0→1
          transition the range zooms forward (scale up) and drops below the
          camera (translateY), mirroring a low-altitude flyover. */}
      <img
        aria-hidden="true"
        src={mountainsImg}
        alt=""
        className="pointer-events-none absolute bottom-0 left-0 h-auto w-full select-none"
        style={{
          opacity: mountainOpacityByStep[stepIdx],
          transform: mountainTransformByStep[stepIdx],
          transformOrigin: "center bottom",
          transition:
            "opacity 1.8s cubic-bezier(0.22, 1, 0.36, 1), transform 1.8s cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "transform, opacity",
        }}
      />

      {/* Bottom warmth for foreground text legibility. */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white/20"
      />
    </div>
  );
}

export function isInkInverted(step: number): boolean {
  const idx = Math.min(step, inkInvertedByStep.length - 1);
  return inkInvertedByStep[idx]!;
}

// ----- Star field --------------------------------------------------------

interface Star {
  x: number;
  y: number;
  size: number;
  delay: number;
  duration: number;
}

const STARS: Star[] = (() => {
  let seed = 1337;
  const rand = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296;
    return seed / 4294967296;
  };
  const out: Star[] = [];
  for (let i = 0; i < 90; i++) {
    out.push({
      x: rand() * 100,
      y: rand() * 70,
      size: 1 + rand() * 2.2,
      delay: rand() * 4,
      duration: 2.5 + rand() * 3.5,
    });
  }
  return out;
})();

function Stars() {
  return (
    <div aria-hidden="true" className="absolute inset-0">
      {STARS.map((s, i) => (
        <div
          key={i}
          className="absolute rounded-full bg-white"
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: `${s.size}px`,
            height: `${s.size}px`,
            boxShadow: "0 0 4px rgba(255,255,255,0.6)",
            animation: `star-twinkle ${s.duration}s ease-in-out ${s.delay}s infinite`,
            willChange: "opacity",
          }}
        />
      ))}
    </div>
  );
}
